import { createFileRoute } from '@tanstack/react-router';
import { useEffect, useRef, useState, type JSX } from 'react';

import MailingDeliveryCard from '@/features/mailing/MailingDeliveryCard.js';
import MailingReviewCard from '@/features/mailing/MailingReviewCard.js';
import type {
  MailingProgress,
  MailingReview,
} from '@/features/mailing/types.js';

export const Route = createFileRoute('/dev/mailing')({
  component: MailingDevPage,
});

/**
 * Dev-only harness for the MEDA-95 mailing cards (no backend yet): renders
 * the review card with sample data; confirming swaps in the delivery card and
 * simulates progress so both states can be exercised end-to-end in the UI.
 */
const SAMPLE_REVIEW: MailingReview = {
  runId: 'dev-run-1',
  templateName: 'Benefits enrollment 2026',
  mode: 'personalized',
  subjectTpl: 'Your 2026 benefits, {{firstName}}',
  bodyTpl:
    'Hi {{firstName}},\n\nOpen enrollment for the {{department}} team runs through Friday. Review your 2026 medical, dental and pension options in the HR portal before the window closes.\n\nYour office ({{station}}) hosts a walk-in Q&A on Wednesday at 14:00.\n\nThanks,\nCT People Team',
  fromName: 'CT People Team',
  fromAddress: 'hr-noreply@customertimes.com',
  recipientCount: 1240,
  audienceDescription: 'Active employees in the IT department · from iCRM',
  sampleRecipients: [
    {
      displayName: 'Anna Kowalska',
      email: 'anna.kowalska@customertimes.com',
      mergeValues: { firstName: 'Anna', department: 'IT', station: 'Warsaw' },
    },
    {
      displayName: 'Marek Wojcik',
      email: 'marek.wojcik@customertimes.com',
      mergeValues: { firstName: 'Marek', department: 'IT', station: 'Krakow' },
    },
    {
      displayName: 'Priya Sharma',
      email: 'priya.sharma@customertimes.com',
      mergeValues: { firstName: 'Priya', department: 'IT', station: 'Pune' },
    },
  ],
  checks: [
    { status: 'ok', text: 'Subject & body ready' },
    { status: 'ok', text: 'Verified sender' },
    { status: 'ok', text: 'Everyone is a Customertimes mailbox' },
    {
      status: 'warn',
      text: '4 people have no office set — we’ll say “your office”',
    },
    { status: 'ok', text: 'Personalization checked' },
  ],
  previewHash: 'dev-hash',
};

function MailingDevPage(): JSX.Element {
  const [progress, setProgress] = useState<MailingProgress | null>(null);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(
    () => () => {
      if (timerRef.current) clearInterval(timerRef.current);
    },
    [],
  );

  const startSimulation = async (): Promise<void> => {
    if (timerRef.current) clearInterval(timerRef.current);
    const total = SAMPLE_REVIEW.recipientCount;
    let sentSoFar = 0;
    setProgress({
      runId: SAMPLE_REVIEW.runId,
      templateName: SAMPLE_REVIEW.templateName,
      status: 'sending',
      total,
      sent: 0,
      failed: 0,
    });
    timerRef.current = setInterval(() => {
      sentSoFar = Math.min(
        total,
        sentSoFar + 60 + Math.round(Math.random() * 40),
      );
      const failed = Math.round(sentSoFar * 0.009);
      setProgress({
        runId: SAMPLE_REVIEW.runId,
        templateName: SAMPLE_REVIEW.templateName,
        status: sentSoFar >= total ? 'completed' : 'sending',
        total,
        sent: sentSoFar - failed,
        failed,
      });
      if (sentSoFar >= total && timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    }, 250);
  };

  const reset = (): void => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    setProgress(null);
  };

  return (
    <div className="mx-auto max-w-2xl px-4 py-8">
      <h1 className="text-lg font-semibold text-foreground">
        Mailing cards — dev harness
      </h1>
      <p className="mt-1 text-sm text-muted-foreground">
        Sample data only. Confirming the review simulates delivery progress.
      </p>
      {progress === null ? (
        <MailingReviewCard
          review={SAMPLE_REVIEW}
          onConfirm={startSimulation}
          onCancel={reset}
          onSendTest={async () => {}}
        />
      ) : (
        <>
          <MailingDeliveryCard progress={progress} onViewFailures={() => {}} />
          <button
            type="button"
            onClick={reset}
            className="mt-4 text-xs font-medium text-muted-foreground underline underline-offset-2 hover:text-foreground"
          >
            Reset harness
          </button>
        </>
      )}
    </div>
  );
}
